import { Field, FieldProps } from 'formik';
import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { IErrorMessage } from '../components/errorMessage/errorMessage.type';
import InputField from '../components/InputField';
import { ValidatorRule } from '../validator/validatorRule.constants';
import { UsernameFieldValidator } from '../validator/usernameFieldValidator';

interface IUsernameFieldProps {
    id?: string;
    labelId?: string;
    userEmail: string;
    autoFocus?: boolean;
    disabled?: boolean;
    intl: InjectedIntl;
    value?: string;
    required?: boolean;
    qaId?: string;
    validateInput: boolean;
    validatorRules?: ValidatorRule[];
    name?: string;
    onUsernameChange?: (inputString: string) => void;
}
interface IUsernameFieldStates {
    focus: boolean;
}
class UsernameField extends React.Component<IUsernameFieldProps, IUsernameFieldStates> {
    private readonly ID: string;
    private readonly LABEL_ID: string;
    private readonly MIN_LENGTH: number;
    private readonly MAX_LENGTH: number;
    private usernameFieldValidator: UsernameFieldValidator;

    constructor(props: IUsernameFieldProps) {
        super(props);

        const {
            id,
            labelId,
            validatorRules = [
                ValidatorRule.SpaceRule,
                ValidatorRule.EUUsernameRangeRule,
                ValidatorRule.LatinCharactersRule,
            ],
        } = props;

        this.state = {
            focus: false,
        };

        this.ID = id ? id : 'username';
        this.LABEL_ID = labelId ? labelId : 'label.username';
        this.MIN_LENGTH = 8;
        this.MAX_LENGTH = 128;

        this.validate = this.validate.bind(this);
        this.renderInputField = this.renderInputField.bind(this);

        this.usernameFieldValidator = new UsernameFieldValidator(validatorRules);
    }

    public render() {
        return <Field name={this.ID} validate={this.validate} render={this.renderInputField} />;
    }

    private validate(value: string): IErrorMessage {
        const { required, userEmail, validateInput } = this.props;

        if (!validateInput) {
            return '';
        }

        if (!value && required) {
            return TranslationHelpers.getMessageDescriptor('error.rakid.missing');
        }

        if (value.toLowerCase() === userEmail.toLowerCase()) {
            return TranslationHelpers.getMessageDescriptor('error.rakid.noemail');
        }

        if (/[^\x00-\x7F]+/g.test(value)) {
            return TranslationHelpers.getMessageDescriptor(
                'error.rakid.letter_number_symbols_point_underscore_midline',
            );
        }
        // Match whether there are characters other than 0-9a-zA-Z._-
        if (/[^0-9a-zA-Z\._-]/g.test(value)) {
            return TranslationHelpers.getMessageDescriptor(
                'error.rakid.letter_number_symbols_point_underscore_midline',
            );
        }
        // Does the matching character contain at least one a-zA-Z character and end with a-zA-Z
        if (!/[a-zA-Z]+/g.test(value)) {
            return TranslationHelpers.getMessageDescriptor(
                'error.rakid_must_include_least_one_letter',
            );
        }

        if (/^[\._-]+|[\._-]+$/g.test(value)) {
            return TranslationHelpers.getMessageDescriptor('error.rakid_not_first_last');
        }

        if (/[\._-]{2,}/g.test(value)) {
            return TranslationHelpers.getMessageDescriptor(
                'error.rakid_not_contain_consecutive_symbols',
            );
        }

        if (value) {
            const err = this.usernameFieldValidator.validate(value, this.LABEL_ID);
            if (err !== null) {
                const errorMessage: IErrorMessage = err.message;
                return errorMessage;
            }
        }

        return '';
    }

    private renderInputField({ form: { touched, errors, handleBlur } }: FieldProps): ReactNode {
        const { autoFocus, disabled, value, required, qaId, onUsernameChange } = this.props;
        const isTouched = touched && touched[this.ID];
        const errorMessage = isTouched && errors && errors[this.ID] ? errors[this.ID] : undefined;
        const labelClassName =
            !!errorMessage || this.state.focus ? 'form-label-focus' : 'form-label';

        const onBlur = (event: React.FocusEvent) => {
            this.setState({
                focus: false,
            });
            handleBlur(event);
        };
        const onFocus = () => {
            this.setState({
                focus: true,
            });
        };
        return (
            <>
                <label id={this.LABEL_ID} className={labelClassName} htmlFor={this.ID}>
                    {TranslationHelpers.getFormattedMessage(this.LABEL_ID)} *
                </label>
                <InputField
                    disabled={disabled}
                    onInputChange={onUsernameChange}
                    type="text"
                    id={this.ID}
                    name={this.ID}
                    value={value}
                    error={errorMessage}
                    autoFocus={autoFocus}
                    required={required}
                    maxLength={this.MAX_LENGTH}
                    data-qa-id={`${qaId}-field`}
                    onBlur={onBlur}
                    onFocus={onFocus}
                />
            </>
        );
    }
}

export default injectIntl(UsernameField);
